import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import 'react-toastify/dist/ReactToastify.css';
import { AuthGuard } from "../App";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faKey, faCheckCircle, faTimesCircle, faSpinner, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'; // Added eye icons for password visibility toggle
import { StandardURL } from "../App";

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState(''); // This state holds the value for the confirm password input
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Frontend validation: Check if new password and confirm new password match
    // This is still good to have for immediate user feedback
    if (newPassword !== confirmNewPassword) {
      toast.error("New password and confirm new password do not match. 🚫");
      setIsLoading(false);
      return;
    }

    // Basic validation for new password strength (you can expand this)
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long. 🔒");
      setIsLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Authentication token not found. Please log in again. 🔑");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.patch(
        `${StandardURL}/iNyumba/users/changepassword`,
        {
          oldPassword,
          newPassword,
          // --- THE KEY CHANGE IS HERE ---
          // Send confirmPassword to the backend as well, with the value from confirmNewPassword state
          confirmPassword: confirmNewPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.success) {
        toast.success(response.data.response || "Password changed successfully! 🎉");
        // Clear form fields on success
        setOldPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        localStorage.clear(); // Clear invalid token
        navigate("/"); // Redirect to login
      } else {
        toast.error(response.data.response || "Failed to change password. Please try again. 😔");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.response || "An unexpected error occurred. Please try again later. 🚧";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 sm:p-6">
        <div className="bg-white p-8 sm:p-10 rounded-3xl shadow-3xl w-full max-w-md animate-fade-in-up border border-gray-100">
          <div className="text-center mb-8">
            <FontAwesomeIcon icon={faLock} className="text-blue-700 text-6xl mb-4 animate-bounce-in" />
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">Change Your Password</h2>
            <p className="text-gray-600 mt-3 text-lg">Strengthen your account security by updating your password.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-7">
            {/* Old Password Field */}
            <div>
              <label htmlFor="old-password" className="block text-gray-700 text-sm font-semibold mb-2">
                <FontAwesomeIcon icon={faKey} className="mr-2 text-gray-500" />
                Current Password:
              </label>
              <div className="relative">
                <input
                  type={showOldPassword ? 'text' : 'password'}
                  id="old-password"
                  className="w-full p-3 pr-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200 outline-none shadow-sm"
                  placeholder="Enter your current password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                  aria-label="Current Password"
                />
                <span
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  aria-label={showOldPassword ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showOldPassword ? faEyeSlash : faEye} />
                </span>
              </div>
            </div>

            {/* New Password Field */}
            <div>
              <label htmlFor="new-password" className="block text-gray-700 text-sm font-semibold mb-2">
                <FontAwesomeIcon icon={faLock} className="mr-2 text-gray-500" />
                New Password:
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="new-password"
                  className="w-full p-3 pr-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200 outline-none shadow-sm"
                  placeholder="Create a strong new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  aria-label="New Password"
                />
                <span
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                </span>
              </div>
            </div>

            {/* Confirm New Password Field (already present and handled by state) */}
            <div>
              <label htmlFor="confirm-new-password" className="block text-gray-700 text-sm font-semibold mb-2">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-gray-500" />
                Confirm New Password:
              </label>
              <div className="relative">
                <input
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  id="confirm-new-password"
                  className="w-full p-3 pr-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200 outline-none shadow-sm"
                  placeholder="Re-enter your new password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                  aria-label="Confirm New Password"
                />
                <span
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                  onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  aria-label={showConfirmNewPassword ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showConfirmNewPassword ? faEyeSlash : faEye} />
                </span>
              </div>
              {newPassword && confirmNewPassword && newPassword !== confirmNewPassword && (
                <p className="text-red-500 text-xs mt-2 flex items-center">
                  <FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> Passwords do not match.
                </p>
              )}
            </div>

            <button
              type="submit"
              className={`w-full py-3.5 px-6 rounded-lg text-lg font-bold flex items-center justify-center transition-all duration-300 ease-in-out transform hover:scale-105
                ${isLoading
                  ? 'bg-blue-400 cursor-not-allowed opacity-80'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 shadow-lg'
                }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-3 text-xl" />
                  Changing Password...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faLock} className="mr-3 text-xl" />
                  Update Password
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </AuthGuard>
  );
};

export default ChangePasswordPage;